import {
  compact,
  postWithTrace,
  get,
  TraceContext,
} from "./httpClient";

export interface MatchGraphRequest {
  graphText: string;
  workspaceId: string;
  projectId: string;
  overrideMatches?: NodeMatch[];
  traceId?: string;  // 再生成時に既存のトレースを継続するためのtrace_id
}

export interface NodeMatch {
  graph_node_id: string;
  graph_node_name: string;
  view_test_target_id?: string;
  view_name?: string;
  confidence: string;
  reason: string;
  path?: string;
}

export interface TransitionInfo {
  item_test_target_id?: string;
  item_name: string;
  target_view_id: string;
  target_view_name: string;
  edge_label?: string;
  confidence: string;
  reason: string;
}

export interface MatchGraphResponse {
  matches: NodeMatch[];
  transitions: TransitionInfo[];
  message: string;
  trace_id?: string;
  traceparent?: string;
}

export interface TransitionUpdate {
  item_test_target_id: string;
  transitions: Array<{
    targetId: string;
    status?: "need_review" | "approved";
  }>;
}

export interface RegisterTransitionsRequest extends TraceContext {
  transitions: TransitionUpdate[];
  workspaceId: string;
  projectId: string;
}

export interface FailedTransitionItem {
  item_test_target_id: string;
  reason: string;
  payload?: Record<string, unknown>;
}

export interface RegisterTransitionsResponse {
  registered_count: number;
  message: string;
  failed_items?: FailedTransitionItem[];
}

export class TransitionGraphService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = "") {
    this.baseUrl = baseUrl;
  }

  async matchGraphWithTargets(
    request: MatchGraphRequest
  ): Promise<MatchGraphResponse> {
    const payload = compact({
      graph_text: request.graphText,
      workspace_id: request.workspaceId,
      project_id: request.projectId,
      override_matches: request.overrideMatches?.map((match) => ({
        graph_node_id: match.graph_node_id,
        graph_node_name: match.graph_node_name,
        view_test_target_id: match.view_test_target_id,
        view_name: match.view_name,
        confidence: match.confidence,
        reason: match.reason,
        path: match.path,
      })),
      trace_id: request.traceId,  // 再生成時に既存のトレースを継続
    });

    return postWithTrace<MatchGraphResponse>(
      this.baseUrl,
      "/transition-graph/match",
      payload
    );
  }

  async registerTransitions(
    request: RegisterTransitionsRequest
  ): Promise<RegisterTransitionsResponse> {
    const payload = compact({
      transitions: request.transitions.map((t) => ({
        item_test_target_id: t.item_test_target_id,
        transitions: t.transitions,
      })),
      workspace_id: request.workspaceId,
      project_id: request.projectId,
      trace_id: request.traceId,
    });

    return postWithTrace<RegisterTransitionsResponse>(
      this.baseUrl,
      "/transition-graph/register",
      payload,
      { traceparent: request.traceparent }
    );
  }

  async createGraphFromLogs(
    request: CreateGraphFromLogsRequest
  ): Promise<CreateGraphFromLogsResponse> {
    const payload = compact({
      workspace_id: request.workspaceId,
      project_id: request.projectId,
      operation_logs: request.operationLogs.map((log) => log.content),
      existing_graph: request.existingGraph?.content,
    });

    return postWithTrace<CreateGraphFromLogsResponse>(
      this.baseUrl,
      "/transition-graph/create-from-logs",
      payload
    );
  }

  async healthCheck(): Promise<any> {
    return get(this.baseUrl, "/transition-graph/health");
  }
}

export interface CreateGraphFromLogsRequest {
  workspaceId: string;
  projectId: string;
  operationLogs: Array<{
    filename: string;
    content: any; // JSON形式の操作ログデータ
  }>;
  existingGraph?: {
    filename: string;
    content: string; // Markdown形式のGraphテキスト
  };
}

export interface CreateGraphFromLogsResponse {
  graph_text: string;
  transitions: Array<{
    from_view?: string;
    to_view: string;
    item_name: string;
    confidence: string;
    reason: string;
  }>;
  statistics: {
    total_logs_processed: number;
    total_transitions_found: number;
    views_identified: number;
  };
  message: string;
}

export const transitionGraphService = new TransitionGraphService("");
